import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import type {
  AgencyResponse,
  UserManagerUserResponse,
} from '../../../services/api/types.gen';
import type { UserRolesState } from '../types';
import { useStore } from '../../../store';
import { useShallow } from 'zustand/react/shallow';
import { PermissionSlug } from '../../../constants/featureFlags';
import {
  EditUserInfoSection,
  type EditUserInfoHandle,
} from '../../../components/rbac/role-assignment-dialog/EditUserInfoSection';

export interface EditUserRolesSave {
  roleIds: string[];
  /** Clinics this user may reach. */
  clinicIds: string[];
  /** Clinic ids per clinic-scoped role id. */
  clinicRoleClinicIds: Record<string, string[]>;
}

export interface EditUserRolesDialogProps {
  open: boolean;
  agency: AgencyResponse | null;
  user: UserManagerUserResponse | null;
  rolesState: UserRolesState;
  onClose: () => void;
  onSave: (save: EditUserRolesSave) => Promise<void>;
}

const EditUserRolesDialog: React.FC<EditUserRolesDialogProps> = ({
  open,
  agency,
  user,
  rolesState,
  onClose,
  onSave,
}) => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );
  const isSuperUser = hasPermission(PermissionSlug.ADMIN_SUPER_USER);

  const editUserInfoRef = useRef<EditUserInfoHandle>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(
    new Set()
  );
  const [allClinicsSelected, setAllClinicsSelected] = useState(true);
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  /**
   * Clinic roles the operator actually clicked. A role they left alone keeps
   * the clinics it came in with, so opening this dialog to change a phone
   * number cannot quietly widen somebody from one clinic to every clinic.
   */
  const [touchedRoleIds, setTouchedRoleIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);

  const allClinicIds = useMemo(
    () => rolesState.clinics.map(c => c.id),
    [rolesState.clinics]
  );

  // A clinic-scoped role is held at every clinic the person can reach or at
  // none of them: the per-clinic grid it used to have made this dialog a
  // matrix nobody could read.
  useEffect(() => {
    const agencyRoleIds = (rolesState.roles ?? []).map(r => r.role_id);
    const clinicRoleIds = Object.entries(rolesState.clinicRoleClinicIds)
      .filter(([, clinicIds]) => clinicIds.length > 0)
      .map(([roleId]) => roleId);
    setSelectedRoleIds(new Set([...agencyRoleIds, ...clinicRoleIds]));
    setTouchedRoleIds(new Set());
  }, [rolesState.roles, rolesState.clinicRoleClinicIds]);

  useEffect(() => {
    const access = rolesState.clinicAccessIds;
    const hasEveryClinic =
      allClinicIds.length > 0 && allClinicIds.every(id => access.includes(id));
    // Reflect what they have, not a friendly default: this dialog exists to
    // repair somebody with no clinic access, and defaulting that to "all"
    // would grant the whole agency on the first save.
    setAllClinicsSelected(hasEveryClinic);
    setSelectedClinicIds(hasEveryClinic ? [] : [...access]);
  }, [rolesState.clinicAccessIds, allClinicIds]);

  const toggleRole = (roleId: string) => {
    setTouchedRoleIds(prev => new Set(prev).add(roleId));
    setSelectedRoleIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  };

  /**
   * The clinics to save a clinic role at.
   *
   * Ticking a role here grants it everywhere, which is the whole point of the
   * all-or-none control. A role the operator never clicked keeps the clinics
   * it already had, narrowed to the access being saved -- a role at a clinic
   * the person can no longer reach would hand that access straight back.
   */
  const clinicsForRole = (roleId: string, clinicIds: string[]): string[] => {
    if (!selectedRoleIds.has(roleId)) return [];
    if (touchedRoleIds.has(roleId)) return clinicIds;
    const held = rolesState.clinicRoleClinicIds[roleId] ?? [];
    if (held.length === 0) return clinicIds;
    return held.filter(id => clinicIds.includes(id));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (isSuperUser && user && editUserInfoRef.current) {
        try {
          await editUserInfoRef.current.saveIfDirty();
        } catch {
          // Error already shown by EditUserInfoSection; stop here
          return;
        }
      }
      const clinicIds = allClinicsSelected ? allClinicIds : selectedClinicIds;
      const clinicRoleIds = new Set(
        rolesState.availableClinicRoles.map(r => r.id)
      );
      // onSave handles its own success toast, error toast, and dialog close
      await onSave({
        roleIds: Array.from(selectedRoleIds).filter(
          id => !clinicRoleIds.has(id)
        ),
        clinicIds,
        clinicRoleClinicIds: Object.fromEntries(
          rolesState.availableClinicRoles.map(role => [
            role.id,
            clinicsForRole(role.id, clinicIds),
          ])
        ),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email
    : '';

  const roleOptions = [
    ...(rolesState.availableRoles ?? []),
    ...rolesState.availableClinicRoles,
  ];

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className='sm:max-w-[540px] flex flex-col max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>Edit roles and clinic access</DialogTitle>
          {user && agency && (
            <p className='text-sm text-muted-foreground mt-1'>
              <span className='font-medium text-foreground'>{fullName}</span>
              {' at '}
              <span className='font-medium text-foreground'>
                {agency.written_name}
              </span>
            </p>
          )}
        </DialogHeader>

        <div className='space-y-5 py-2 overflow-y-auto min-h-0 flex-1'>
          {isSuperUser && user && (
            <EditUserInfoSection
              ref={editUserInfoRef}
              userId={user.id}
              firstName={user.first_name}
              lastName={user.last_name}
              phoneNumber={user.phone_number}
              isRogerEmployee={user.is_roger_employee ?? false}
            />
          )}

          {/* Roles */}
          <div className='space-y-2'>
            <Label>Roles</Label>
            <p className='text-xs text-muted-foreground'>
              Ticking a clinic role applies it at every clinic below. One left
              alone keeps the clinics it already has.
            </p>
            {rolesState.isLoading ? (
              <div className='flex items-center justify-center gap-2 border rounded-md p-6 text-muted-foreground'>
                <Loader2 className='h-4 w-4 animate-spin' />
                <span className='text-sm'>Loading roles…</span>
              </div>
            ) : rolesState.readTruncated ? (
              /* Without this the screen reads as an agency with no roles,
                 and the disabled Save button looks like a bug. */
              <div className='border border-destructive/50 rounded-md p-4 text-sm text-destructive'>
                This agency has more clinics or role assignments than this
                dialog can read at once, so it is showing only part of them.
                Saving would revoke the rest, so editing is disabled here.
              </div>
            ) : (
              <div className='space-y-2 max-h-56 overflow-y-auto border rounded-md p-3'>
                {roleOptions.map(role => (
                  <div key={role.id} className='flex items-start gap-2'>
                    <Checkbox
                      id={`edit-role-${role.id}`}
                      checked={selectedRoleIds.has(role.id)}
                      onCheckedChange={() => toggleRole(role.id)}
                      disabled={isSaving}
                    />
                    <div className='flex flex-col'>
                      <Label
                        htmlFor={`edit-role-${role.id}`}
                        className='font-medium cursor-pointer'
                      >
                        {role.name}
                      </Label>
                      {role.description && (
                        <span className='text-xs text-muted-foreground'>
                          {role.description}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {roleOptions.length === 0 && (
                  <p className='text-sm text-muted-foreground text-center py-2'>
                    No roles available for this agency.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Clinic access — the same control as the add-user dialog. */}
          {!rolesState.isLoading && rolesState.clinics.length > 0 && (
            <div className='space-y-2'>
              <Label>Clinic access</Label>
              <div className='space-y-2 max-h-56 overflow-y-auto border rounded-md p-3'>
                <div className='flex items-start gap-2'>
                  <Checkbox
                    id='edit-clinics-all'
                    checked={allClinicsSelected}
                    onCheckedChange={checked => {
                      const all = checked === true;
                      setAllClinicsSelected(all);
                      if (all) setSelectedClinicIds([]);
                    }}
                    disabled={isSaving}
                  />
                  <Label
                    htmlFor='edit-clinics-all'
                    className='font-medium cursor-pointer'
                  >
                    All clinics
                  </Label>
                </div>
                {!allClinicsSelected &&
                  rolesState.clinics.map(clinic => (
                    <div
                      key={clinic.id}
                      className='flex items-start gap-2 pl-6'
                    >
                      <Checkbox
                        id={`edit-clinic-${clinic.id}`}
                        checked={selectedClinicIds.includes(clinic.id)}
                        onCheckedChange={() =>
                          setSelectedClinicIds(prev =>
                            prev.includes(clinic.id)
                              ? prev.filter(id => id !== clinic.id)
                              : [...prev, clinic.id]
                          )
                        }
                        disabled={isSaving}
                      />
                      <Label
                        htmlFor={`edit-clinic-${clinic.id}`}
                        className='cursor-pointer'
                      >
                        {clinic.name}
                      </Label>
                    </div>
                  ))}
              </div>
              {!allClinicsSelected && selectedClinicIds.length === 0 && (
                <p className='text-xs text-muted-foreground'>
                  No clinics selected — this person will not see any clinic's
                  patients.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={handleSave}
            // Every save here reconciles, so saving a load that failed would
            // write the empty screen over the person's real roles and access
            // -- the exact state this dialog exists to repair.
            disabled={isSaving || rolesState.isLoading || !rolesState.loaded}
          >
            {isSaving && <Loader2 className='h-4 w-4 animate-spin mr-2' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserRolesDialog;
